import { RangeBar } from '../molecules/RangeBar';
import { RANGE_BAR_TRACK_BLEED_X } from '../../constants/rangeBarLayout';
import type {
  RangeBarEventsProps,
  RangeBarRangeProps,
  RangeSelectorAppearanceProps,
  RangeSelectorFormattingProps,
  RangeSelectorLabelsProps,
} from '../../types';
import { cn } from '@/shared/utils/cn';
import { forwardRef, useMemo } from 'react';
import {
  formattedValueCharWidth,
  maxFormattedTickStringLength,
} from '../../utils/rangeSelectorDisplayWidth';

export interface RangeSelectorProps {
  /** Cabecera visible (Figma: «Label»). */
  labels: RangeSelectorLabelsProps;
  /** Dominio y valor del intervalo. */
  range: RangeBarRangeProps;
  /** Emisión de cambios de valor. */
  events: RangeBarEventsProps;
  /** Color del carril y variante compacta. */
  appearance?: RangeSelectorAppearanceProps;
  /** Formato numérico y separador «de» en cabecera. */
  formatting?: RangeSelectorFormattingProps;
  /** Deshabilita el control. */
  disabled?: boolean;
  /** Clases en el contenedor raíz (ref). */
  className?: string;
}

function defaultFormatValue(n: number): string {
  return String(n);
}

/**
 * **RangeSelector** — etiquetas + valores actuales + `RangeBar` (Figma `2:264`).
 *
 * Incluye variante **compacta** (slider entre etiquetas de `min`/`max`) y la variante
 * ampliada con carril a ancho completo entre cabecera y leyendas inferiores. En compacto, los
 * extremos numéricos usan ancho en `ch` **por lado** (`min` y `max` por separado) y se alinean
 * hacia la pista (no un único ancho compartido del dígito más largo). En modo ampliado,
 * cabecera y leyendas inferiores comparten el mismo sangrado horizontal que el carril del
 * `RangeBar` ({@link RANGE_BAR_TRACK_BLEED_X}) para alinear texto con inicio y fin de la pista.
 * Los números formateados (cabecera y pie) reservan ancho fijo en unidades ch y tipografía
 * tabular (CSS) para que no «salten» al cambiar el valor.
 *
 * @example
 * ```tsx
 * const [v, setV] = useState({ start: 2.5, end: 4 });
 * <RangeSelector
 *   labels={{ title: 'Precio' }}
 *   range={{ min: 0, max: 5, step: 0.5, value: v }}
 *   events={{ onChange: setV }}
 * />
 * ```
 */
export const RangeSelector = forwardRef<HTMLDivElement, RangeSelectorProps>(function RangeSelector(
  {
    labels,
    range,
    events,
    appearance: appearanceProp,
    formatting: formattingProp,
    disabled = false,
    className,
  },
  ref
) {
  const { title } = labels;
  const { min, max, value, step = 1 } = range;
  const { onChange } = events;
  const { color = 'brand', compact = false } = appearanceProp ?? {};
  const { formatValue = defaultFormatValue, currentRangeSeparator = 'de' } = formattingProp ?? {};

  const fmt = formatValue;
  const gap = compact ? 'ui-gap-2' : 'ui-gap-8';

  /** Ancho fijo (ch) para valores numéricos: evita saltos al cambiar cifras. */
  const numericDisplayChars = useMemo(
    () => maxFormattedTickStringLength(min, max, step, fmt),
    [fmt, max, min, step]
  );
  const numericSlotStyle = useMemo(
    () =>
      ({
        width: `${numericDisplayChars}ch`,
        minWidth: `${numericDisplayChars}ch`,
        fontVariantNumeric: 'tabular-nums',
      }) as const,
    [numericDisplayChars]
  );

  const makeNumericSlotStyle = (chars: number) =>
    ({
      width: `${chars}ch`,
      minWidth: `${chars}ch`,
      fontVariantNumeric: 'tabular-nums',
    }) as const;

  /** Compacto: cada extremo solo ocupa el ancho de su cifra (evita empujar `0` cuando `max` es largo). */
  const compactMinSlotStyle = useMemo(
    () => makeNumericSlotStyle(formattedValueCharWidth(min, fmt)),
    [fmt, min]
  );
  const compactMaxSlotStyle = useMemo(
    () => makeNumericSlotStyle(formattedValueCharWidth(max, fmt)),
    [fmt, max]
  );

  const rangeBarAccessibility = {
    'aria-label': `Rango de ${title}`,
  } as const;

  const header = (
    <div
      className={cn(
        'ui-flex ui-w-full ui-items-center ui-gap-16',
        !compact && RANGE_BAR_TRACK_BLEED_X
      )}
    >
      <div
        className={cn(
          'ui-min-h-0 ui-min-w-0 ui-flex-1',
          'ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs',
          'ui-text-primary'
        )}
      >
        {title}
      </div>
      <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-4">
        <span
          className={cn(
            'ui-inline-block ui-whitespace-nowrap ui-text-right',
            'ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs',
            'ui-text-secondary'
          )}
          style={numericSlotStyle}
        >
          {fmt(value.start)}
        </span>
        <span
          className={cn(
            'ui-whitespace-pre',
            'ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs',
            'ui-text-secondary'
          )}
        >
          {` ${currentRangeSeparator} `}
        </span>
        <span
          className={cn(
            'ui-inline-block ui-whitespace-nowrap ui-text-left',
            'ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs',
            'ui-text-secondary'
          )}
          style={numericSlotStyle}
        >
          {fmt(value.end)}
        </span>
      </div>
    </div>
  );

  /** Leyenda `min`: en compacto el texto va al borde que toca la pista (derecha del bloque). */
  const labelMin = (
    <div
      className={cn(
        'ui-flex ui-flex-none ui-flex-col ui-justify-center',
        compact ? 'ui-items-end ui-text-right' : 'ui-items-start ui-text-left',
        'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
        'ui-text-secondary'
      )}
      style={compact ? compactMinSlotStyle : numericSlotStyle}
    >
      {fmt(min)}
    </div>
  );

  /** Leyenda `max`: en compacto el texto va al borde que toca la pista (izquierda del bloque). */
  const labelMax = (
    <div
      className={cn(
        'ui-flex ui-flex-none ui-flex-col ui-justify-center',
        compact ? 'ui-items-start ui-text-left' : 'ui-items-end ui-text-right',
        'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
        'ui-text-secondary'
      )}
      style={compact ? compactMaxSlotStyle : numericSlotStyle}
    >
      {fmt(max)}
    </div>
  );

  const rangeBarShared = {
    range: { min, max, value, step },
    events: { onChange },
    appearance: { color },
    accessibility: rangeBarAccessibility,
    disabled,
  } as const;

  const compactRow = (
    <div className="ui-flex ui-w-full ui-items-center ui-gap-16">
      {labelMin}
      <RangeBar {...rangeBarShared} className="ui-min-w-0 ui-flex-1" trackBleed={false} />
      {labelMax}
    </div>
  );

  const footerLabelsOnly = (
    <div
      className={cn(
        'ui-flex ui-w-full ui-items-center ui-justify-between',
        RANGE_BAR_TRACK_BLEED_X
      )}
    >
      {labelMin}
      {labelMax}
    </div>
  );

  return (
    <div ref={ref} className={cn('ui-imj ui-flex ui-w-full ui-flex-col', gap, className)}>
      {header}
      {!compact && (
        <>
          <RangeBar {...rangeBarShared} />
          {footerLabelsOnly}
        </>
      )}
      {compact && compactRow}
    </div>
  );
});

RangeSelector.displayName = 'RangeSelector';
